
import { Form, Formik } from 'formik';
import { omit } from 'lodash';
import React from 'react';
import * as Yup from 'yup';
import { CheckFieldGroup } from 'shared/formik/CheckFieldGroup';
import { DateTimeField } from 'shared/formik/DateTimeField';
import { EnumOptions, SelectFieldGroup } from 'shared/formik/SelectFieldGroup';
import { TextFieldGroup } from 'shared/formik/TextFieldGroup';
import { MutationErrors } from '../../../shared/components/MutationErrors';
import { PageContainer } from 'shared/components/PageContainer';
import { RentalPageTitleHelmet } from 'rental/components/PageTitleHelmet';
import { SubmitActions } from '../../../shared/components/SubmitActions';
import { PageHeading } from 'shared/components/typography';
import { AboutMeForm_CustomerFragment, AboutMeFragment, useUpdateCustomerMutation } from '../../generated.graphql';
import { getSubmitActionsProps } from 'shared/forms/helpers';
import { cleanPhoneNumber } from 'shared/utils/phone';
import { RentalStepProps } from 'rental/app/steps';
import { yupMobilePhoneNumber, yupRequiredMsg } from '../../../shared/yup';
import { PrivacyPolicyModalButton } from 'rental/app/PrivacyPolicy';
import { useAppContext } from 'rental/app/AppContext';


interface Props extends RentalStepProps {
  application: AboutMeFragment;
  portalSlug: string;
  titleEnum: any;
}

interface Values extends AboutMeForm_CustomerFragment {
  marketingConsent: boolean;
}

const validationSchema = Yup.object({
  title: Yup.string().nullable().required(yupRequiredMsg),
  firstName: Yup.string().required(yupRequiredMsg),
  lastName: Yup.string().required(yupRequiredMsg),
  email: Yup.string().email('Enter a valid email address.').required(yupRequiredMsg),
  phoneMobile: yupMobilePhoneNumber.required(yupRequiredMsg),
  dob: Yup.date().required("Date of birth must be a valid date"),
  licenceNo: Yup.string(),
  marketingConsent: Yup.boolean(),
});

export const AboutMeForm = (props: Props) => {
  const { application } = props;
  const { customer } = application;
  const [, executeMutation] = useUpdateCustomerMutation();
  const app = useAppContext();

  return (
    <div>
      <PageContainer>
        <RentalPageTitleHelmet>About Me</RentalPageTitleHelmet>
        <PageHeading>About Me</PageHeading>
          {customer && 
            <Formik<Values>
              initialValues={{
                ...customer,
                dob: customer.dob ?? '',
                marketingConsent: application.marketingConsent
              }}
              validationSchema={validationSchema}
              validateOnChange={true}
              onSubmit={async (values, formikHelpers) => {
                const r = await executeMutation({
                  input: {
                    ...omit(values, ['__typename']),
                    portalSlug: props.portalSlug,
                    dob: values.dob || null,  // Need to specify null, not empty string or graphql server dies
                    phoneMobile: cleanPhoneNumber(values.phoneMobile),
                  }
                });
                
                if (r.data?.updateCustomer?.application) {
                  props.onSubmitSuccessful();
                }
                else {
                  formikHelpers.setStatus(
                    [
                      ...r.data?.updateCustomer?.errors || [],
                      ...r.data?.updateCustomer?.application?.validation.customer || []
                    ] || null)
                  props.onSubmitFailure();
                  window.scrollTo(0, 0);
                }
                
              }}
            >
              {formik => 
                <Form noValidate>
                  {formik.status && <MutationErrors errors={formik.status}/>}
                  <div className="my-4">
                    <div className="form-row">
                      <div className="col-sm">
                        <div className="form-row">
                          <SelectFieldGroup name="title" label="Title" blank={true} custom className="col-auto">
                            <EnumOptions enum={props.titleEnum} />
                          </SelectFieldGroup>
                          <TextFieldGroup label="First name" name="firstName" className="col col-sm"/>
                        </div>
                      </div>
                      <TextFieldGroup label="Last name" name="lastName" className="col-sm"/>
                    </div>
                    <div className="form-row">
                      <TextFieldGroup label="Email" name="email" type="email" className="col-sm" formNoValidate autoComplete="off" />
                      <TextFieldGroup label="Mobile number" name="phoneMobile" type="tel" pattern="\d*" formNoValidate className="col-sm" />
                    </div>
                    <div className="form-row">
                      {/* TODO: Probably swap out widgets based on date support */}
                      <DateTimeField label="Date of Birth" name="dob" whenNull="" className="col-md" />
                      <div className="col-sm">
                        <div className="d-none d-md-inline-block mb-2">&nbsp;</div>
                        <TextFieldGroup label="Drivers license number" name="licenceNo" className="px-0" />
                      </div>
                    </div>
                  </div>

                  {!application.inStore && (
                    <div className="form-row">
                      <div className="form-group col-sm">
                        <CheckFieldGroup name="marketingConsent" custom="switch">
                          Sign up to receive the latest news, special offers and product information and more.&nbsp;<br className='d-none d-md-inline'/>
                          You may unsubscribe at any time using the link provided in the communication.{' '}
                          View our <a href={app.portal.policies.privacyPolicyUrl} target="_blank" rel="nofollow noreferrer" className=""><u>Privacy Policy</u></a>.
                        </CheckFieldGroup>
                      </div>
                    </div>
                  )}

                  <SubmitActions {...getSubmitActionsProps(formik, props)}/>

                </Form>
              }
          </Formik>
        }
      </PageContainer>
    </div>
  )
}
